import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const ScreenMasterContext = createContext({
  loading: true,
  screens: [],
  enabledMap: {},
  isEnabled: () => true,
  isNavKeyEnabled: () => true,
  refresh: async () => {},
  toggle: async () => {}
});

export function ScreenMasterProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [screens, setScreens] = useState([]);
  const [enabledMap, setEnabledMap] = useState({});

  const applyPayload = useCallback((data) => {
    if (!data) return;
    setScreens(Array.isArray(data.screens) ? data.screens : []);
    setEnabledMap(data.enabledMap && typeof data.enabledMap === 'object' ? data.enabledMap : {});
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get('/api/screen-master', { timeout: 12000 });
      applyPayload(res.data);
    } catch {
      /* keep last known */
    } finally {
      setLoading(false);
    }
  }, [applyPayload]);

  const toggle = useCallback(async (id, enabled) => {
    const res = await axios.post('/api/screen-master/toggle', { id, enabled }, { timeout: 15000 });
    if (res.data?.state) applyPayload(res.data.state);
    else await refresh();
    return res.data;
  }, [applyPayload, refresh]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const isEnabled = useCallback((id) => {
    if (id === 'screen-master') return true;
    if (!enabledMap || Object.keys(enabledMap).length === 0) return true;
    return enabledMap[id] !== false;
  }, [enabledMap]);

  const isNavKeyEnabled = useCallback((navKey) => {
    if (!screens.length) return true;
    const hit = screens.find((s) => (s.navKeys || []).includes(navKey));
    if (!hit) return true;
    return hit.enabled !== false;
  }, [screens]);

  const value = useMemo(() => ({
    loading,
    screens,
    enabledMap,
    isEnabled,
    isNavKeyEnabled,
    refresh,
    toggle
  }), [loading, screens, enabledMap, isEnabled, isNavKeyEnabled, refresh, toggle]);

  return (
    <ScreenMasterContext.Provider value={value}>
      {children}
    </ScreenMasterContext.Provider>
  );
}

export function useScreenMaster() {
  return useContext(ScreenMasterContext);
}
